import axios from "axios";
import { parsePhoneNumber, PhoneNumber } from "libphonenumber-js";
import React, { useContext, useState } from "react";
import OtpInput from "react-otp-input";
import ResendOtp from "../../../apps/Website/pages/Checkin/components/ReseneOtp";
import Button from "../../../components/ui/Button";
import { MobileInput } from "../../../components/ui/Form";
import { AuthContext } from "../../../contexts/auth";
import { ToastContext } from "../../../contexts/toast";
import { AuthContext as ZoAuthContext } from "../../../contexts/zoauth";
import {
  useUnauthenticatedApi,
  useZoUnauthenticatedApi,
} from "../../../hooks/useApi";
import { getApiEndpoint } from "../../../utils/api";

interface LoginProps {
  closeModal?: () => void;
}

const Login: React.FC<LoginProps> = ({ closeModal }) => {
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [hasRequestedOTP, setRequestOTP] = useState<boolean>(false);
  const [isRequestingOTP, setIsRequestingOTP] = useState<boolean>(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState<boolean>(false);

  const { login, logout } = useContext(AuthContext);
  const {
    login: loginZo,
    deviceId,
    deviceSecret,
    logout: logoutZo,
  } = useContext(ZoAuthContext);
  const { addToast } = useContext(ToastContext);

  const requestOTPApi = useZoUnauthenticatedApi("AUTH_LOGIN_MOBILE_OTP");
  const activateZo = useZoUnauthenticatedApi("AUTH_LOGIN_MOBILE");
  const activateZostel = useUnauthenticatedApi("AUTH_ACTIVATE");

  const isMobileNumberValid = (number: string): boolean => {
    try {
      const phoneNumber = parsePhoneNumber(`+${number}`);
      return phoneNumber.isValid();
    } catch {
      return false;
    }
  };

  const handleRequestOTP = async (messageChannel: string) => {
    if (!isMobileNumberValid(mobileNumber)) {
      addToast("Please enter a valid mobile number.");
      return;
    }

    try {
      setIsRequestingOTP(true);

      const phoneNumber = parsePhoneNumber(`+${mobileNumber}`) as PhoneNumber;

      const data = {
        mobile_number: phoneNumber.nationalNumber,
        mobile_country_code: phoneNumber.countryCallingCode,
        message_channel: messageChannel === "whatsapp" ? "whatsapp" : "",
      };

      const response = await requestOTPApi.post("/", data);

      if (response.data.success) {
        setRequestOTP(true);
        addToast("OTP has been sent to your mobile number.");
      } else {
        addToast("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      addToast("Failed to send OTP. Please try again.");
    } finally {
      setIsRequestingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!mobileNumber) {
      addToast("Mobile number is missing.");
      return;
    }

    if (otp.length !== 6) {
      addToast("Please enter a valid 6-digit OTP.");
      return;
    }

    try {
      setIsVerifyingOTP(true);
      const phoneNumber = parsePhoneNumber(`+${mobileNumber}`) as PhoneNumber;
      if (!phoneNumber.isValid()) {
        addToast("Invalid phone number format.");
        return;
      }
      const otpVerificationData = {
        mobile_number: phoneNumber.nationalNumber,
        mobile_country_code: phoneNumber.countryCallingCode,
        otp,
      };

      const zoResponse = await activateZo.post("/", otpVerificationData);
      const { token: zoToken, valid_till, user: zoUser } = zoResponse.data;

      loginZo(zoToken, valid_till, zoUser);

      const zostelCredsResponse = await axios.post(
        `${getApiEndpoint("AUTH_REQUEST_OTP_ZOSTEL")}`,
        {},
        {
          headers: {
            "client-device-id": deviceId,
            "client-device-secret": deviceSecret,
            "client-key": process.env.REACT_APP_CLIENT_KEY,
            "Content-Type": "application/json",
            Authorization: `Bearer ${zoToken}`,
          },
        }
      );

      const {
        mobile_number: zostelMobile,
        mobile_country_code: zostelCountryCode,
        code: zostelOtp,
      } = zostelCredsResponse?.data || {};

      const zostelVerifyResponse = await activateZostel.post("/", {
        mobile: zostelMobile,
        mobile_country_code: zostelCountryCode,
        otp: zostelOtp,
      });

      const {
        user_token: zostelToken,
        token_expiry: zostelExpiry,
        user: zostelUser,
      } = zostelVerifyResponse.data;

      login(zostelToken, zostelExpiry, zostelUser);

      closeModal?.();
    } catch (error) {
      addToast("Invalid OTP. Try again later.");
      logout();
      logoutZo();
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleEditNumber = () => {
    setRequestOTP(false);
    setOtp("");
  };

  return !hasRequestedOTP ? (
    <div className="px-6 w-full">
      <div className=" mt-4">
        <div className="flex flex-col items-start">
          <MobileInput
            className="form-element-mobile w-full"
            value={mobileNumber}
            setter={setMobileNumber}
          />
          <Button
            className="w-full mt-6"
            size="lg"
            onClick={() => handleRequestOTP("")}
            disabled={!isMobileNumberValid(mobileNumber) || isRequestingOTP}
          >
            Send OTP
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex flex-col mt-4 space-y-4">
      <p className="text-primary text-base text-center">
        Enter Otp sent on{" "}
        <strong className="text-primary font-medium">+{mobileNumber}</strong>{" "}
        <button className="text-primary" onClick={handleEditNumber}>
          <i className="uil uil-edit" />
        </button>
      </p>
      <div className="flex items-start justify-center">
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span>&nbsp;&nbsp;</span>}
          inputStyle={{
            borderWidth: 1,
            borderColor: "#11111129",
            borderRadius: 2,
            height: 44,
            width: 44,
            outline: "none",
          }}
          renderInput={(props) => <input {...props} />}
        />
      </div>
      <Button
        size="lg"
        onClick={handleVerifyOTP}
        disabled={otp.length !== 6 || isVerifyingOTP}
      >
        Login
      </Button>
      <ResendOtp onPress={handleRequestOTP} />
    </div>
  );
};

export default Login;
